import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCallStore } from '../store/useCallStore';
import { useAuthStore } from '../store/useAuthStore';
import { FRONTEND_VERSION, fetchBackendVersion } from '../api/version';
import { adminGetCompanies } from '../api/admin';
import type { Company } from '../types/auth';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useCallHistoryLoader } from '../hooks/useCallHistoryLoader';
import FilterBar from './FilterBar';
import CallTable from './CallTable';
import MiniCallList from './MiniCallList';

// Die eigentliche Anruflisten-Ansicht inkl. Laden/Auto-Refresh. Auf der Admin-Seite
// (#/admin) ist diese Komponente komplett unmounted — kein Hintergrund-Polling dort.

interface Props {
  onLogout: () => void;
  onChangePassword: () => void;
  msToExpiry: number | null;
  refreshing: boolean;
  onRefreshToken: () => void;
}

export default function CallHistoryView({ onLogout, onChangePassword, msToExpiry, refreshing, onRefreshToken }: Props) {
  const {
    filter, error, loading,
    darkMode, toggleDarkMode, setFilter,
  } = useCallStore();
  const { user, company } = useAuthStore();
  const { loadCallHistory, warmingTick, warmingProgress } = useCallHistoryLoader();

  const [backendVersion, setBackendVersion] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchBackendVersion().then((v) => setBackendVersion(v.version)).catch(() => undefined);
  }, []);

  // Admins: Unternehmen für die „Als Unternehmen ansehen"-Auswahl laden
  useEffect(() => {
    if (user?.role !== 'admin') return;
    adminGetCompanies().then(setCompanies).catch(() => undefined);
  }, [user?.role]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onRefresh: user ? loadCallHistory : () => undefined,
    onFocusSearch: () => searchInputRef.current?.focus(),
    onClearSearch: () => setFilter({ search: '' }),
  });

  // ── Mini-Ansicht ──
  // Chrome/Edge: Document Picture-in-Picture = schwebendes Always-on-top-Fenster,
  // das sich Store + WebSocket mit diesem Tab teilt (React-Portal). Andere Browser:
  // klassisches Popup auf #/mini (eigenständige Instanz).
  const pipSupported = 'documentPictureInPicture' in window;

  function openMiniPopupFallback() {
    window.open(`${window.location.pathname}#/mini`, 'evesMini', 'popup=yes,width=420,height=640');
  }

  async function openMiniView() {
    if (!pipSupported) { openMiniPopupFallback(); return; }
    try {
      const dpip = (window as unknown as {
        documentPictureInPicture: { requestWindow: (opts: { width: number; height: number }) => Promise<Window> };
      }).documentPictureInPicture;
      const pip = await dpip.requestWindow({ width: 400, height: 560 });
      // Styles der Hauptseite ins PiP-Fenster übernehmen
      for (const sheet of Array.from(document.styleSheets)) {
        try {
          if (sheet.href) {
            const link = pip.document.createElement('link');
            link.rel = 'stylesheet';
            link.href = sheet.href;
            pip.document.head.appendChild(link);
          } else if (sheet.ownerNode instanceof HTMLStyleElement) {
            const style = pip.document.createElement('style');
            style.textContent = sheet.ownerNode.textContent ?? '';
            pip.document.head.appendChild(style);
          }
        } catch { /* fremde Stylesheets ignorieren */ }
      }
      pip.document.title = `${(viewedCompany ?? company)?.name ?? 'eves_MDR'} – Anrufhistorie`;
      pip.document.documentElement.setAttribute('data-bs-theme', darkMode ? 'dark' : 'light');
      pip.document.body.style.margin = '0';
      pip.addEventListener('pagehide', () => setPipWindow(null));
      setPipWindow(pip);
    } catch {
      openMiniPopupFallback();
    }
  }

  // Dark-Mode und Titel ins PiP-Fenster spiegeln; beim Verlassen Fenster schließen
  useEffect(() => {
    pipWindow?.document.documentElement.setAttribute('data-bs-theme', darkMode ? 'dark' : 'light');
  }, [darkMode, pipWindow]);
  useEffect(() => {
    if (pipWindow) pipWindow.document.title = `${(viewedCompany ?? company)?.name ?? 'eves_MDR'} – Anrufhistorie`;
  });
  useEffect(() => () => { pipWindow?.close(); }, [pipWindow]);

  const viewedCompany = filter.viewAsCompanyId ? companies.find((c) => c.id === filter.viewAsCompanyId) : null;

  const initials = (user?.displayName ?? '?')
    .split(/\s+/)
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const logoSvg = (viewedCompany ?? company)?.logoSvg ?? null;
  const defaultLogo = (
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 16 16" fill="#0d6efd">
      <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.6 17.6 0 0 0 4.168 6.608 17.6 17.6 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.68.68 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.68.68 0 0 0-.122-.58z" />
    </svg>
  );

  return (
    <div className="min-vh-100 bg-body-tertiary">
      <header className="bg-body border-bottom shadow-sm py-3 px-4 mb-4">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            {logoSvg ? (
              <img
                src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(logoSvg)}`}
                alt="Logo"
                style={{ height: 36, width: 'auto', maxWidth: 80, objectFit: 'contain' }}
              />
            ) : defaultLogo}
            <div>
              <h1 className="h5 mb-0 fw-bold text-primary">
                {(viewedCompany ?? company)?.name ?? 'eves_MDR'} – Anrufhistorie
              </h1>
              <p className="mb-0 small text-muted">
                {viewedCompany
                  ? <span className="text-info">Nur-Lese-Ansicht als „{viewedCompany.name}"</span>
                  : <>Eingehende Anrufe &amp; Rückruf-Status</>}
              </p>
            </div>
          </div>

          <div className="d-flex align-items-center gap-1 flex-wrap">
            <button
              className={`btn btn-sm header-icon-btn ${pipWindow ? 'header-icon-active' : ''}`}
              title={pipSupported
                ? 'Mini-Ansicht öffnen — schwebt immer im Vordergrund'
                : 'Mini-Ansicht als kleines Fenster öffnen'}
              onClick={() => (pipWindow ? pipWindow.close() : openMiniView())}
            >
              📌<span className="d-none d-md-inline ms-1">Mini</span>
            </button>

            <button
              className="btn btn-sm header-icon-btn"
              title={darkMode ? 'Hellen Modus aktivieren' : 'Dunklen Modus aktivieren'}
              onClick={toggleDarkMode}
            >
              {darkMode ? '☀️' : '🌙'}
            </button>

            {user?.role === 'admin' && (
              <a className="btn btn-sm header-icon-btn" href="#/admin" title="Administration öffnen">
                ⚙️<span className="d-none d-md-inline ms-1">Admin</span>
              </a>
            )}

            <div className="vr mx-2 opacity-25 d-none d-sm-block" style={{ height: 26 }} />

            <div className="d-flex align-items-center gap-2 me-1" title={`Version ${FRONTEND_VERSION}${backendVersion && backendVersion !== FRONTEND_VERSION ? ` · Backend ${backendVersion}` : ''}`}>
              <span className="avatar-circle" aria-hidden>{initials}</span>
              <div className="lh-sm d-none d-sm-block">
                <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{user?.displayName}</div>
                <div className="text-muted" style={{ fontSize: '0.65rem' }}>
                  Version {FRONTEND_VERSION}
                  {backendVersion && backendVersion !== FRONTEND_VERSION && <> · BE {backendVersion}</>}
                </div>
              </div>
            </div>

            <button className="btn btn-sm header-icon-btn" title="Passwort ändern" onClick={onChangePassword}>
              🔑
            </button>

            <button className="btn btn-sm header-icon-btn header-btn-danger" title="Abmelden" onClick={onLogout}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" aria-hidden>
                <path fillRule="evenodd" d="M10 12.5a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v2a.5.5 0 0 0 1 0v-2A1.5 1.5 0 0 0 9.5 2h-8A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-2a.5.5 0 0 0-1 0z" />
                <path fillRule="evenodd" d="M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708z" />
              </svg>
              <span className="d-none d-md-inline ms-1">Abmelden</span>
            </button>
          </div>
        </div>
      </header>

      <main className="container-fluid px-4">
        <FilterBar onRefresh={loadCallHistory} loading={loading} searchInputRef={searchInputRef} companies={companies} />

        {msToExpiry !== null && msToExpiry < 15 * 60 * 1000 && (
          <div className="alert alert-warning d-flex align-items-center gap-3 mb-3 no-print" role="alert">
            <span className="flex-grow-1">
              Ihre Sitzung läuft in{' '}
              <strong>
                {msToExpiry < 60_000 ? 'weniger als 1 Minute' : `${Math.ceil(msToExpiry / 60_000)} Minuten`}
              </strong>{' '}
              ab.
            </span>
            <button className="btn btn-sm btn-warning text-nowrap" onClick={onRefreshToken} disabled={refreshing}>
              {refreshing ? 'Verlängern…' : 'Jetzt verlängern'}
            </button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger d-flex align-items-center gap-3 mb-4" role="alert">
            <span className="flex-grow-1">{error}</span>
            <button className="btn btn-sm btn-outline-danger text-nowrap" onClick={loadCallHistory}>
              Erneut versuchen
            </button>
          </div>
        )}

        {warmingTick > 0 && !error && (
          <div className="alert alert-info mb-4" role="status">
            <div className="d-flex align-items-center gap-3">
              <span className="spinner-border spinner-border-sm flex-shrink-0" aria-hidden="true" />
              <span className="flex-grow-1">
                Die Anrufhistorie für diesen Zeitraum wird gerade vom Telefonsystem geladen —
                die neuesten Anrufe werden unten bereits angezeigt, ältere kommen automatisch dazu.
                {warmingProgress && warmingProgress.fetchedRows > 0 && (
                  <> Bisher {warmingProgress.fetchedRows.toLocaleString('de-DE')} Anrufe geladen
                    {warmingProgress.coveredFrom && <>, zurück bis {warmingProgress.coveredFrom.slice(0, 16)}</>}
                    {warmingProgress.percent !== null && <> (~{Math.round(warmingProgress.percent * 100)} %)</>}
                    {warmingProgress.etaSeconds !== null && warmingProgress.etaSeconds > 30 && (
                      <>, Restdauer ca. {Math.max(1, Math.round(warmingProgress.etaSeconds / 60))} Min.</>
                    )}.
                  </>
                )}
              </span>
            </div>
            {warmingProgress?.percent !== null && warmingProgress?.percent !== undefined && (
              <div className="progress mt-2" style={{ height: 6 }}>
                <div className="progress-bar progress-bar-striped progress-bar-animated bg-info" style={{ width: `${Math.max(3, Math.round(warmingProgress.percent * 100))}%` }} />
              </div>
            )}
          </div>
        )}

        <div className="card shadow-sm border-0 rounded-3">
          <div className="card-body p-0">
            <CallTable />
          </div>
        </div>
      </main>

      {pipWindow && createPortal(<MiniCallList />, pipWindow.document.body)}
    </div>
  );
}
